import { useInternational } from '@/common/locale';
import { ToggleInfo, TargetData, isCategory } from '@/ai/utils/types';
import { ref, computed } from 'vue';
import { makeRows } from '@/common/utils/gridUtils';
import { CustomColumn } from '@/common/utils';
import { useDetailAdapterByCategory } from '@/common/composables/useOpenDetailByCategory';

export interface Props {
  targetData: TargetData[];
}

const allowedKinds = [
  'infrastructure',
  'application',
  'database',
  'host',
  'container',
  'rum',
  'kubernetes',
  'aws',
  'kafka',
];

export const setup = (props: Props) => {
  const { t } = useInternational();

  const { openDetailByCategory } = useDetailAdapterByCategory();

  const toggleValue = ref<ToggleInfo>('list');
  const onChange = (newVal: ToggleInfo) => {
    toggleValue.value = newVal;
  };

  const columns: CustomColumn[] = [
    { caption: 'Platform', field: 'targetKind', type: 'string', width: 115 },
    { caption: 'Target Name', field: 'targetName', type: 'string', width: 140 },
    {
      caption: 'Alert',
      field: 'targetAlertLevel',
      type: 'string',
      rendererType: 'capitalize-label',
      width: 105,
    },
    {
      caption: 'Target Id',
      field: 'targetId',
      hide: true,
    },
  ];

  const convertedRows = computed(() => {
    return makeRows(props.targetData || [], columns);
  });

  const mappedTargetData = computed(() => {
    return props.targetData.reduce(
      (acc, item) => {
        const category = allowedKinds.includes(item.targetKind) ? item.targetKind : 'etc';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);

        return acc;
      },
      {} as Record<string, Props['targetData']>,
    );
  });

  const getIconType = (targetKind: string) => {
    return allowedKinds.includes(targetKind) ? `icon-ai-${targetKind}` : 'icon-document-etc';
  };

  const onDetail = async (targetInfo: string[] | TargetData) => {
    let targetKind: string | undefined;
    let targetName: string | undefined;
    let targetId: string | undefined;

    if (Array.isArray(targetInfo)) {
      [targetKind, targetName, , targetId] = targetInfo;
    } else if (typeof targetInfo === 'object' && targetInfo !== null) {
      ({ targetId, targetName, targetKind } = targetInfo);
    }

    if (!targetKind || !isCategory(targetKind)) {
      console.log('디테일 연계 실패: 타겟 종류가 없습니다.');
      return;
    }

    try {
      await openDetailByCategory(targetKind, {
        params: {
          targetId,
          targetName,
        },
      });
    } catch (error: unknown) {
      console.error(`${targetKind} 카테고리 처리 중 오류:`, error);
    }
  };

  return {
    t,
    columns,
    toggleValue,
    convertedRows,
    mappedTargetData,
    onChange,
    onDetail,
    getIconType,
  };
};
